import { useMutation } from "@tanstack/react-query";
import isWithinStudyArea from "../constants/studyArea";
import i18n from "../i18n";

// -------------------------------------------------------------
// useLocationSearch
// Takes a location name typed by the user,
// sends it to the Nominatim API to get
// coordinates, then validates the coordinates
// against the study area boundary.
//
// Why useMutation and not useQuery?
// - useQuery runs automatically when a component mounts
// - this search should only run when the user submits the form
// - useMutation gives us manual control over when the request starts
//
// Why mutateAsync and not mutate?
// - mutate is fire-and-forget — you can't await it
// - mutateAsync returns a promise, so we can await it in handleSearch
// - this matters because handleSearch needs to wait for the search
//   to finish before the result panel can show anything meaningful
//
// Why wrap mutateAsync in searchLocation?
// - mutateAsync throws loudly if something goes wrong
// - without a wrapper, that error flies straight into handleSearch
//   which has no try/catch — crashing the app
// - the wrapper quietly catches the error here so handleSearch never
//   sees it. React Query already stores the error in the error state
//   which the component reads and shows as a toast message
//
// Returns:
// - searchLocation: function to trigger the search
// - coordinates: the lat/lng of the found location
// - locationName: the full formatted place name
// - isLoading: true while the search is in progress
// - error: error message if something went wrong
// - reset: clears all state back to initial
// -------------------------------------------------------------

// Handles the full location search request and validation flow.
const fetchLocationSearch = async (query) => {
  try {
    // Send the typed location name to Nominatim.
    // Nominatim converts the text into coordinates.
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=1&addressdetails=1&accept-language=en`,
    );

    if (!response.ok) {
      throw new Error(`⚠️ ${i18n.t("locationAndGps.searchRequestError")}`);
    }

    const data = await response.json();

    // If Nominatim found no matching location, stop with a friendly error.
    if (!data || data.length === 0) {
      throw new Error(`⚠️ ${i18n.t("locationAndGps.locationNotFound")}`);
    }

    // Extract the coordinates from the first result.
    const lat = parseFloat(data?.[0]?.lat);
    const lng = parseFloat(data?.[0]?.lon);

    // Study area validation.
    // Reuse the same Kaduna/Kano boundary check used elsewhere in the app.
    if (!isWithinStudyArea(lat, lng)) {
      throw new Error(`⚠️ ${i18n.t("locationAndGps.locationOutsideStudyArea")}`);
    }

    // Build the formatted place name using the same style as usePlaceName.
    const address = data?.[0]?.address ?? {};
    const localName =
      address.village || address.town || address.city || address.county || "Unknown area";
    const state = address.state || "Unknown state";
    const country = address.country || "Unknown country";

    return {
      coordinates: { lat, lng },
      locationName: `${localName}, ${state}, ${country}`,
    };
  } catch (err) {
    // Browsers often surface Nominatim throttling or temporary blocking
    // as a generic "Failed to fetch" network error.
    if (err instanceof Error && err.message === "Failed to fetch") {
      throw new Error(`⚠️ ${i18n.t("locationAndGps.locationServiceBusy")}`);
    }

    // Re-throw known errors so React Query can store them.
    if (err instanceof Error) {
      throw err;
    }

    // Normalize any unexpected thrown value into a safe Error instance.
    throw new Error(`⚠️ ${i18n.t("locationAndGps.somethingWentWrong")}`);
  }
};

const useLocationSearch = () => {
  // mutateAsync is used over mutate because handleSearch needs to await
  // the result — it has to wait for the search to finish before the
  // result panel can show anything. Like standing there waiting for a
  // reply before moving on.
  const { mutateAsync, data, error, isPending, reset } = useMutation({
    mutationFn: fetchLocationSearch,
  });

  // Wraps mutateAsync so any errors are caught here and never reach the
  // caller. React Query already stores the error in the error state above,
  // so the component just reads that and shows it as a toast — no crash.
  const searchLocation = async (query) => {
    try {
      await mutateAsync(query);
    } catch (err) {
      return;
    }
  };

  return {
    searchLocation,
    coordinates: data?.coordinates ?? null,
    locationName: data?.locationName ?? null,
    isLoading: isPending,
    error: error?.message ?? null,
    reset,
  };
};

export default useLocationSearch;
